import type { ReactNode } from "react";

import { cn } from "@/shadcn/lib/utils";

/** True iPhone 16 logical dimensions, for previews laid out at device scale. */
export const DEVICE_PREVIEW = { width: 393, height: 852 } as const;

type PhoneFrameProps = {
  readonly children: ReactNode;
  readonly cropHeight?: number;
  readonly className?: string;
  /**
   * Lay the content out at true device size and scale the whole frame down,
   * instead of the default fixed 280px shell.
   *
   * The catalog previews need this: sizing against a miniature scale means
   * guessing every type proportion, where at device size the patient app's own
   * `type-*` values can be used directly and the mock is honest by construction.
   */
  readonly scale?: number;
  /**
   * Scaled frames only: float the camera island over the content, as on a real device, so a full-bleed page
   * (its own background reaching the top edge) is not capped by a white band.
   */
  readonly islandOverContent?: boolean;
};

export const PhoneFrame = ({
  children,
  cropHeight,
  className,
  scale,
  islandOverContent = false,
}: PhoneFrameProps) => {
  if (scale) {
    return (
      // Outer box reserves the post-scale footprint so surrounding layout is honest.
      <div
        className={className}
        style={{ height: DEVICE_PREVIEW.height * scale, width: DEVICE_PREVIEW.width * scale }}
      >
        <div
          style={{
            height: DEVICE_PREVIEW.height,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
            width: DEVICE_PREVIEW.width,
          }}
        >
          <div className="relative flex h-full flex-col overflow-hidden rounded-[55px] border-[10px] border-neutral-900 bg-white">
            <div
              className={cn(
                "flex shrink-0 justify-center pt-2 pb-1",
                islandOverContent ? "absolute inset-x-0 top-0 z-10" : "bg-white",
              )}
            >
              <div className="h-9 w-32 rounded-full bg-neutral-900" />
            </div>
            {children}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn("relative mx-auto w-[280px]", className)}
      style={cropHeight ? { maxHeight: cropHeight, overflow: "hidden" } : undefined}
    >
      <div className="overflow-hidden rounded-[40px] border-[6px] border-neutral-900 bg-white">
        <div className="flex justify-center pt-2 pb-1">
          <div className="h-[14px] w-[72px] rounded-full bg-neutral-900" />
        </div>

        <div className="mb-1 overflow-hidden rounded-b-[32px] bg-white">{children}</div>

        <div className="flex justify-center pt-1 pb-2">
          <div className="h-[4px] w-[100px] rounded-full bg-neutral-300" />
        </div>
      </div>
    </div>
  );
};
